import { Button, ButtonVariant } from '@repo/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@repo/ui/dialog'
import { useT } from '@repo/ui/hooks'
import { Muted } from '@repo/ui/muted'
import { Separator } from '@repo/ui/separator'
import { MessagesSquare, QrCodeIcon } from 'lucide-react'
import QRCode from 'qrcode'
import { useRef, useState } from 'react'

import { buildBeneficiaryAppUrlFromBeneficiaryId } from '@/components/features/chat-sessions/url.ts'
import QrCode from '@/components/QrCode'
import { storeActions } from '@/store/actions'
import { Beneficiary } from '@/store/beneficiary'
import { ChatSession } from '@/store/chat'
import { useStoreDispatch } from '@/store/hooks'
import { Organization } from '@/store/user'

const generateQR = async (url: string): Promise<string | null> => {
  try {
    const code = await QRCode.toDataURL(url, { errorCorrectionLevel: 'H' })
    return code
  } catch (err) {
    console.error(err)
    return null
  }
}

export function QrCodeViewer({
  beneficiary,
  chatSessions,
  organization,
  variant = 'outline',
}: {
  beneficiary: Beneficiary
  chatSessions: ChatSession[]
  organization: Organization
  variant?: ButtonVariant
}): JSX.Element {
  const dispatch = useStoreDispatch()
  const { t } = useT({ keyPrefix: 'chat.modal.codeViewer' })
  const [open, setOpen] = useState(false)
  const [qRCode, setQrCode] = useState<string>()
  const [url, setUrl] = useState<string>()
  const getQRCode = async () => {
    const url = buildBeneficiaryAppUrlFromBeneficiaryId({
      beneficiaryId: beneficiary.id,
    })
    console.info('Chat Session URL: ', url)
    setUrl(url)
    const code = await generateQR(url)
    if (code) setQrCode(code)
  }
  const qrCodeRef = useRef<HTMLDivElement | null>(null)
  const handlePrint = () => {
    const printWindow = window.open('', '_blank')
    if (printWindow && qrCodeRef.current) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${organization.name}_QR_Code_${beneficiary.fullName}</title>
            <style>
              @media print {
                body {
                  margin: 0;
                  margin-top: 20px;
                  padding: 0;
                  display: flex;
                  flex-direction: column; 
                  justify-content: center;
                  width: 100vw;
                }
              }
              p {
                max-width: 50vw;
                text-align: center;
              }
              div {
                display: flex;
                margin: 5px 0;
                justify-content: center;
                width: 100vw;
              }
            </style>
          </head>
          <body>
            <div>
              <p><b>${organization.name}</b> - QR Code: ${beneficiary.fullName}</p>
            </div>
            <div>
              <p>${t('description')}</p>
            </div>
            <div>
              ${qrCodeRef.current.outerHTML}
            </div>
            <div>
              <p>${url}</p>
            </div>
          </body>
        </html>
      `)
      printWindow.document.close()
      printWindow.focus()
      printWindow.print()
    }
  }
  const handleClick = async () => {
    if (chatSessions.length === 0)
      await dispatch(
        storeActions.beneficiary.chatSession.create({
          beneficiaryId: beneficiary.id,
        }),
      )
    await getQRCode()
    setOpen(true)
  }
  return (
    <Dialog open={open} onOpenChange={setOpen} defaultOpen={open}>
      <DialogTrigger asChild>
        <Button variant={variant} onClick={() => void handleClick()} className="w-fit">
          {chatSessions.length === 0 ? (
            <>
              <MessagesSquare className="size-5" />
              {t('enableChat')}
            </>
          ) : (
            <>
              <QrCodeIcon className="size-5" />
              {t('cta')}
            </>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>
        <Separator />
        <div className="flex flex-col items-center justify-center gap-4">
          <div className="text-center" ref={qrCodeRef}>
            {qRCode && <QrCode base64Data={qRCode} />}
          </div>
          <Button className="mb-2" onClick={handlePrint}>
            {t('print')}
          </Button>
          <Separator />
          <Muted className="text-center">{url}</Muted>
        </div>
      </DialogContent>
    </Dialog>
  )
}
